import React from 'react';
import { HealthResponse } from '../types/api';
import { useProcessedStats } from '../hooks/useProcessedStats';

interface HealthPanelProps {
  health: HealthResponse | null;
  loading: boolean;
  error: string | null;
  // Function to manually refresh health status
  onRefresh: () => void;
}

const HealthPanel: React.FC<HealthPanelProps> = ({ health, loading, error, onRefresh }) => {
  const { stats, loading: statsLoading, error: statsError, lastRefresh, refetch } = useProcessedStats();
  
  const formatBytes = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const formatUptime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatNumber = (numStr: string): string => {
    const num = parseInt(numStr, 10);
    return num.toLocaleString();
  };

  if (error) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 className="panel-title" style={{ margin: 0 }}>System Health</h2>
          <button
            onClick={onRefresh}
            disabled={loading}
            style={{
              padding: '6px',
              backgroundColor: 'transparent',
              color: loading ? '#6c757d' : '#495057',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              transition: 'all 0.2s ease'
            }}
            title="Refresh health status"
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = '#e9ecef';
                e.currentTarget.style.color = '#007bff';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#495057';
              }
            }}
          >
            {loading ? (
              <div style={{
                width: '14px',
                height: '14px',
                border: '2px solid #6c757d',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="m3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
            )}
          </button>
        </div>
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 className="panel-title" style={{ margin: 0 }}>System Health</h2>
          <button
            onClick={onRefresh}
            disabled={loading}
            style={{
              padding: '6px',
              backgroundColor: 'transparent',
              color: loading ? '#6c757d' : '#495057',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              transition: 'all 0.2s ease'
            }}
            title="Refresh health status"
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = '#e9ecef';
                e.currentTarget.style.color = '#007bff';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#495057';
              }
            }}
          >
            <span className="loading-spinner" style={{ margin: 0 }} />
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <span className="loading-spinner" />
          Loading health status...
        </div>
      </div>
    );
  }

  if (!health) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 className="panel-title" style={{ margin: 0 }}>System Health</h2>
          <button
            onClick={onRefresh}
            disabled={loading}
            style={{
              padding: '6px',
              backgroundColor: 'transparent',
              color: loading ? '#6c757d' : '#495057',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              transition: 'all 0.2s ease'
            }}
            title="Refresh health status"
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = '#e9ecef';
                e.currentTarget.style.color = '#007bff';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#495057';
              }
            }}
          >
            {loading ? (
              <div style={{
                width: '14px',
                height: '14px',
                border: '2px solid #6c757d',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="m3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
            )}
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
          No health data available
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 className="panel-title" style={{ margin: 0 }}>System Health</h2>
        <button
          onClick={onRefresh}
          disabled={loading}
          style={{
            padding: '6px',
            backgroundColor: 'transparent',
            color: loading ? '#6c757d' : '#495057',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            transition: 'all 0.2s ease'
          }}
          title="Refresh health status"
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = '#e9ecef';
              e.currentTarget.style.color = '#007bff';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#495057';
            }
          }}
        >
          {loading ? (
            <div style={{
              width: '14px',
              height: '14px',
              border: '2px solid #6c757d',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"></polyline>
              <polyline points="1 20 1 14 7 14"></polyline>
              <path d="m3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
          )}
        </button>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
        {/* System Overview */}
        <div style={{ padding: '20px', border: '1px solid #e9ecef', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#495057' }}>System Overview</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div><strong>Status:</strong> <span style={{ color: health.status === 'healthy' ? '#28a745' : '#dc3545', fontWeight: '500' }}>{health.status}</span></div>
            <div><strong>Uptime:</strong> {formatUptime(health.uptime)}</div>
            <div><strong>Last Check:</strong> {new Date(health.timestamp).toLocaleString()}</div>
          </div>
        </div>

        {/* Memory Usage */}
        <div style={{ padding: '20px', border: '1px solid #e9ecef', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#495057' }}>Memory Usage</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div><strong>Heap Used:</strong> {formatBytes(health.memory.heapUsed)}</div>
            <div><strong>Heap Total:</strong> {formatBytes(health.memory.heapTotal)}</div>
            <div><strong>RSS:</strong> {formatBytes(health.memory.rss)}</div>
            <div><strong>External:</strong> {formatBytes(health.memory.external)}</div>
          </div>
        </div>

        {/* PostgreSQL Connections */}
        <div style={{ padding: '20px', border: '1px solid #e9ecef', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#495057' }}>PostgreSQL Connections</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div><strong>Total:</strong> {health.connectionPools.postgres.totalCount}</div>
            <div><strong>Idle:</strong> {health.connectionPools.postgres.idleCount}</div>
            <div><strong>Waiting:</strong> {health.connectionPools.postgres.waitingCount}</div>
          </div>
        </div>

        {/* SQL Server Connections */}
        <div style={{ padding: '20px', border: '1px solid #e9ecef', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#495057' }}>SQL Server Connections</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div><strong>Connected:</strong> <span style={{ color: health.connectionPools.sqlServer.connected ? '#28a745' : '#dc3545', fontWeight: '500' }}>{health.connectionPools.sqlServer.connected ? 'Yes' : 'No'}</span></div>
            <div><strong>Healthy:</strong> <span style={{ color: health.connectionPools.sqlServer.healthy ? '#28a745' : '#dc3545', fontWeight: '500' }}>{health.connectionPools.sqlServer.healthy ? 'Yes' : 'No'}</span></div>
            <div><strong>Pool Size:</strong> {health.connectionPools.sqlServer.size}</div>
            <div><strong>Available:</strong> {health.connectionPools.sqlServer.available}</div>
            <div><strong>Pending:</strong> {health.connectionPools.sqlServer.pending}</div>
            <div><strong>Borrowed:</strong> {health.connectionPools.sqlServer.borrowed}</div>
          </div>
        </div>

        {/* Processed Stats */}
        <div style={{ padding: '20px', border: '1px solid #e9ecef', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, color: '#495057' }}>In Last 24 Hours Processed </h3>
            <button
              onClick={refetch}
              disabled={statsLoading}
              style={{
                padding: '6px',
                backgroundColor: 'transparent',
                color: statsLoading ? '#6c757d' : '#495057',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                cursor: statsLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                transition: 'all 0.2s ease'
              }}
              title="Refresh processed stats"
              onMouseEnter={(e) => {
                if (!statsLoading) {
                  e.currentTarget.style.backgroundColor = '#e9ecef';
                  e.currentTarget.style.color = '#007bff';
                }
              }}
              onMouseLeave={(e) => {
                if (!statsLoading) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#495057';
                }
              }}
            >
              {statsLoading ? (
                <div style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid #6c757d',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"></polyline>
                  <polyline points="1 20 1 14 7 14"></polyline>
                  <path d="m3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                </svg>
              )}
            </button>
          </div>
          
          {statsError ? (
            <div style={{ color: '#dc3545', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
              <strong>Error:</strong> {statsError}
            </div>
          ) : statsLoading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid #e9ecef',
                borderTop: '2px solid #007bff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 10px'
              }} />
              Loading stats...
            </div>
          ) : stats ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div><strong>Countries:</strong> {formatNumber(stats.total_countries)}</div>
                <div><strong>Tracks:</strong> {formatNumber(stats.total_tracks)}</div>
                <div><strong>Events:</strong> {formatNumber(stats.total_events)}</div>
                <div><strong>Horses:</strong> {formatNumber(stats.total_horses)}</div>
              </div>
              {lastRefresh && (
                <div style={{ marginTop: '10px', fontSize: '12px', color: '#6c757d', textAlign: 'center' }}>
                  Last Run: {lastRefresh.toLocaleString()}
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
              No processed stats available
            </div>
          )}
        </div>
      </div>

      {/* Connection Health Indicators */}
      <div style={{ marginTop: '30px', padding: '20px', border: '1px solid #e9ecef', borderRadius: '8px' }}>
        <h3 style={{ margin: '0 0 15px 0', color: '#495057' }}>Connection Status</h3>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              borderRadius: '50%', 
              backgroundColor: health.connectionPools.postgres.totalCount > 0 ? '#28a745' : '#dc3545' 
            }}></div>
            <span>PostgreSQL</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              borderRadius: '50%', 
              backgroundColor: health.connectionPools.sqlServer.connected && health.connectionPools.sqlServer.healthy ? '#28a745' : '#dc3545' 
            }}></div>
            <span>SQL Server</span>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '20px', fontSize: '0.9rem', color: '#6c757d', textAlign: 'center' }}>
        Health status refreshes automatically every 6 minutes
      </div>
    </div>
  );
};

export default HealthPanel;